import { ActionListContent } from 'modules/automation-rules/actions/components/action-list-content'
import { actionTitleLocoKeys } from 'modules/automation-rules/actions/constants/action-loco-keys'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { withErrorBoundary } from 'shared/hocs/with-error-boundary'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { getWorkflowEditorUrl } from 'shared/utils/get-workflow-editor-url'
import { ContactId } from '../../types/contact-interface'
import { useContactWorkflows } from '../hooks/use-contact-workflows'
import { ContactWorkflowInterface } from '../types/contact-workflow-interface'
import { CheckCircleIcon, Hint } from '@systemeio/ui'

interface ContactWorkflowsProps {
  contactId: ContactId
}

const ContactWorkflows = ({ contactId }: ContactWorkflowsProps) => {
  const { t } = useLocoTranslation()
  const { limit, pagination, setLimit, setPagination } = usePagination(true)
  const { contactWorkflows } = useContactWorkflows({ contactId, limit, pagination })

  const columns: ColumnDefinitionInterface<ContactWorkflowInterface>[] = [
    {
      key: 'workflowStep',
      widthPercent: 50,
      header: t('dashboard.contact_page.workflows.headers.name'),
      onRender: ({ workflowStep: { workflow } }) => (
        <a
          href={getWorkflowEditorUrl(workflow.id)}
          rel="noreferrer"
          className="primary-link w-full whitespace-normal"
          target="__blank"
        >
          {workflow.name}
        </a>
      ),
    },
    {
      key: 'workflowStep',
      widthPercent: 50,
      header: t('dashboard.contact_page.workflows.headers.current_step'),
      onRender: ({ workflowStep: { action }, isCompleted }) =>
        isCompleted ? (
          <span className="flex items-center gap-2">
            <CheckCircleIcon className="fill-green" />
            {t('dashboard.contact_page.workflows.finished_workflow')}
          </span>
        ) : (
          <div className="flex flex-col whitespace-normal">
            <span>{action ? t(actionTitleLocoKeys[action.type]) : ''}</span>
            <span>{action && <ActionListContent action={action} />}</span>
          </div>
        ),
    },
  ]

  return (
    <ListingLayout
      counter={false}
      data={contactWorkflows}
      titleClassName={'truncate'}
      title={
        <Hint
          className={'truncate'}
          label={t('dashboard.contact_page.workflows.description')}
        >
          <h2 className={'h-[42px] truncate text-[17px] font-bold leading-[42px]'}>
            {t('header.top_menu.workflows')}
          </h2>
        </Hint>
      }
      type="compact"
    >
      <Table
        columns={columns}
        data={contactWorkflows}
        paginator={{ pagination, setPagination }}
        limiter={{ limit, setLimit }}
        nestedId="workflowStep"
      />
    </ListingLayout>
  )
}

export default withErrorBoundary(ContactWorkflows)
